import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { clientDashboardService } from '../../services/clientDashboard';

interface Props {
  filters: { periodo: string; dataInicio?: string; dataFim?: string };
  refreshKey: number;
}

function formatSeconds(s: number): string {
  if (!s || s <= 0) return '0s';
  const mins = Math.floor(s / 60);
  const secs = Math.round(s % 60);
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}

const ClientDashboardPerformance: React.FC<Props> = ({ filters, refreshKey }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await clientDashboardService.getPerformance(filters);
        setData(res.data);
      } catch (err: any) {
        if (err.response?.status !== 401) {
          setError('Erro ao carregar dados de performance. Tente novamente.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filters.periodo, filters.dataInicio, filters.dataFim, refreshKey]);

  if (loading) {
    return (
      <div className="client-dashboard-loading">
        <div className="client-dashboard-spinner" />
        Carregando performance...
      </div>
    );
  }

  if (error) {
    return (
      <div className="client-dashboard-error-message">{error}</div>
    );
  }

  const porHora = (data?.conferencias_por_hora || []).map((item: any) => ({
    hora: new Date(item.hora).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
    total: item.total
  }));

  return (
    <>
      <h2 className="client-dashboard-section-title">Indicadores de Performance Operacional</h2>

      <div className="client-dashboard-metrics-grid">
        <div className="client-dashboard-metric-big">
          <div className="client-dashboard-metric-big-value">
            {formatSeconds(data?.tempo_medio_conferencia || 0)}
          </div>
          <div className="client-dashboard-metric-big-label">Tempo Médio de Conferência</div>
        </div>

        <div className="client-dashboard-metric-big">
          <div className="client-dashboard-metric-big-value">
            {formatSeconds(data?.tempo_medio_bipagem || 0)}
          </div>
          <div className="client-dashboard-metric-big-label">Tempo Médio de Bipagem</div>
        </div>

        <div className="client-dashboard-metric-big">
          <div className="client-dashboard-metric-big-value">
            {formatSeconds(data?.tempo_medio_liberacao || 0)}
          </div>
          <div className="client-dashboard-metric-big-label">Tempo Médio de Liberação</div>
        </div>
      </div>

      <div className="client-dashboard-charts">
        <div className="client-dashboard-chart-container">
          <h3 className="client-dashboard-chart-title">Conferências por Hora</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={porHora}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
              <XAxis dataKey="hora" stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" />
              <Tooltip
                contentStyle={{
                  background: '#1e293b',
                  border: '1px solid rgba(148,163,184,0.2)',
                  borderRadius: '8px',
                  color: '#e2e8f0'
                }}
              />
              <Line
                type="monotone"
                dataKey="total"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ fill: '#3b82f6', r: 4 }}
                name="Leituras"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="client-dashboard-chart-container">
          <h3 className="client-dashboard-chart-title">Volume de Conferência</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={porHora}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
              <XAxis dataKey="hora" stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" />
              <Tooltip
                contentStyle={{
                  background: '#1e293b',
                  border: '1px solid rgba(148,163,184,0.2)',
                  borderRadius: '8px',
                  color: '#e2e8f0'
                }}
              />
              <Bar dataKey="total" fill="#06b6d4" radius={[4, 4, 0, 0]} name="Leituras" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
};

export default ClientDashboardPerformance;
